// app/src/features/signals/tsdPaths.js
// Pure SVG path geometry for the time-series chart (TsdChart): one plotted series → its past /
// future lines, the fill under the past, the confidence cone, the impact line and the branch
// overlays, all in the timeline's current framing. `useTsdGeometry` memoises these per render.
import { BRANCH_COLORS, R_START } from "../../lib/timeline/geometry.js";

/**
 * The chart paths of one plotted series.
 *
 * @param {string} k  the series name (the key in `series`)
 * @param {object} s  the series entry (`data`, `yMin`, `yMax`, `color`)
 * @param {string} sColor  the slot color (theme-gated pair, else the entry's own)
 * @param {object} geom
 * @param {number} geom.tsdH  the chart's height
 * @param {number} geom.ppd  pixels per day
 * @param {number} geom.viewLeft  the day at the left edge
 * @param {number} geom.todayX  the today indicator's x
 * @param {Date} geom.timelineToday  the clock `todayX` is derived from
 * @param {object|null} geom.active  the active forecast (its `seriesImpacts`)
 * @param {Array} geom.branches  the active forecast's branch runs
 */
export function seriesSlotPaths(
  k,
  s,
  sColor,
  { tsdH, ppd, viewLeft, todayX, timelineToday, active, branches },
) {
  if (!s.data || !s.data.length)
    return {
      k,
      pastPath: "",
      futurePath: "",
      fill: "",
      confidenceFill: "",
      color: sColor,
      impactPath: "",
      branchPaths: [],
      startX: 0,
      endX: 0,
      futureEndX: 0,
    };
  const mn = s.yMin,
    mx = s.yMax,
    range = mx - mn || 1,
    yv = (v) => 4 + (tsdH - 8) * (1 - (v - mn) / range),
    clampY = (v) => yv(Math.max(mn, Math.min(mx, v)));
  const sx = (d) => ((new Date(d) - R_START) / 86400000 - viewLeft) * ppd;
  // Split points strictly by the TIMELINE's "today", regardless of how the server tagged them:
  // some FRED series ship "past"-tagged points dated past today (extrapolated historical
  // updates), which need to render as future (dim), not past (bright). A synthetic anchor at
  // TODAY bridges the two lines so they meet cleanly at the today indicator.
  //
  // Use `timelineToday` — the SAME clock `todayX` is derived from — NOT `effectiveToday`:
  // when the user scrubs to a past reforecast vintage, `timelineToday` rewinds to that
  // vintage's moment while `effectiveToday` stays on wall-clock. Splitting/clipping the
  // fill at one clock while closing it at the other would wrap the area diagonally back.
  // Using the vintage moment for both lands the fill on the today line and rewinds the
  // whole TSD chart with the rest of the timeline (matching the probability plot, which
  // also runs on timelineToday).
  const TODAY_MS = timelineToday.getTime();
  const allPoints = [...s.data].sort((a, b) => new Date(a.date) - new Date(b.date));
  const past = allPoints.filter((p) => new Date(p.date).getTime() <= TODAY_MS);
  const future = allPoints.filter((p) => new Date(p.date).getTime() > TODAY_MS);
  let pastForLine = past;
  let futureForLine = future;
  let todayAnchor = null;
  if (past.length > 0 && future.length > 0) {
    const lp = past[past.length - 1];
    const ff = future[0];
    const lpMs = new Date(lp.date).getTime();
    const ffMs = new Date(ff.date).getTime();
    if (lpMs < TODAY_MS && ffMs > TODAY_MS) {
      const t = (TODAY_MS - lpMs) / (ffMs - lpMs);
      const tv = lp.value + t * (ff.value - lp.value);
      todayAnchor = { date: new Date(TODAY_MS), value: tv };
      pastForLine = [...past, todayAnchor];
      futureForLine = [todayAnchor, ...future];
    } else {
      // One side lands exactly on TODAY — future bridges from past's last point.
      futureForLine = [lp, ...future];
    }
  }
  const pastPath = pastForLine
    .map((p, i) => `${i === 0 ? "M" : "L"}${sx(p.date).toFixed(1)},${yv(p.value).toFixed(1)}`)
    .join(" ");
  let futurePath = "";
  if (futureForLine.length) {
    futurePath = futureForLine
      .map((p, i) => `${i === 0 ? "M" : "L"}${sx(p.date).toFixed(1)},${yv(p.value).toFixed(1)}`)
      .join(" ");
  }
  // Clip the fill to dates ≤ today and interpolate the right edge: when the upstream `points`
  // array extends into the future, tracing through those points and then closing back to
  // (todayX, tsdH) wraps the path backward, giving a diagonal slope on the fill's right edge.
  let fillPts = pastForLine.filter((p) => new Date(p.date).getTime() <= TODAY_MS);
  if (fillPts.length) {
    const last = fillPts[fillPts.length - 1];
    const lastMs = new Date(last.date).getTime();
    if (lastMs < TODAY_MS) {
      const next =
        pastForLine.find((p) => new Date(p.date).getTime() > TODAY_MS) ||
        future.find((p) => new Date(p.date).getTime() > TODAY_MS);
      if (next) {
        const nMs = new Date(next.date).getTime();
        const t = nMs > lastMs ? (TODAY_MS - lastMs) / (nMs - lastMs) : 0;
        fillPts = [
          ...fillPts,
          { date: new Date(TODAY_MS), value: last.value + t * (next.value - last.value) },
        ];
      }
    }
  }
  const fill = fillPts.length
    ? `M${sx(fillPts[0].date)},${yv(fillPts[0].value)} ${fillPts.map((p) => `L${sx(p.date)},${yv(p.value)}`).join(" ")} L${todayX},${tsdH} L${sx(fillPts[0].date)},${tsdH}Z`
    : "";
  // The band at "today" is interpolated from the projection's opening sample (dated at the
  // last observation) to the first future sample. A payload without an opening sample
  // pinches to zero width at the last past point.
  let confidenceFill = "";
  const bandsData = future.filter((p) => p.upper != null && p.lower != null);
  if (bandsData.length >= 2) {
    const lastPast = past[past.length - 1];
    const openingBand =
      past.filter((p) => p.upper != null && p.lower != null).pop() ||
      (lastPast && { date: lastPast.date, upper: lastPast.value, lower: lastPast.value });
    let bridge = [];
    if (openingBand) {
      const first = bandsData[0];
      const oMs = new Date(openingBand.date).getTime();
      const fMs = new Date(first.date).getTime();
      const t = fMs > oMs ? Math.min(1, Math.max(0, (TODAY_MS - oMs) / (fMs - oMs))) : 0;
      bridge = [
        {
          date: new Date(Math.max(oMs, TODAY_MS)),
          upper: openingBand.upper + t * (first.upper - openingBand.upper),
          lower: openingBand.lower + t * (first.lower - openingBand.lower),
        },
      ];
    }
    const allBands = [...bridge, ...bandsData];
    const upper = allBands.map((p) => `${sx(p.date).toFixed(1)},${clampY(p.upper).toFixed(1)}`);
    const lower = allBands
      .slice()
      .reverse()
      .map((p) => `${sx(p.date).toFixed(1)},${clampY(p.lower).toFixed(1)}`);
    confidenceFill = `M${upper[0]} ${upper
      .slice(1)
      .map((c) => "L" + c)
      .join(" ")} ${lower.map((c) => "L" + c).join(" ")}Z`;
  }
  let impactPath = "";
  const impacts = active?.seriesImpacts?.[k] || [];
  if (impacts.length && future.length) {
    const pts = future.map((d) => {
      let delta = 0;
      for (const imp of impacts) if (new Date(imp.date) <= d.date) delta = imp.delta_percent;
      const av = d.value * (1 + delta / 100);
      return { x: sx(d.date), y: clampY(av) };
    });
    if (pts.length)
      impactPath = pts
        .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
        .join(" ");
  }
  // Branches are run objects from active's store tree: seriesImpacts/forkDate map 1:1, br.id
  // → br.runId, and the branch order fixes the BRANCH_COLORS index.
  const branchPaths = branches
    .map((br, bi) => {
      const bImpacts = br.seriesImpacts?.[k] || [];
      const forkD = br.forkDate ? new Date(br.forkDate) : null;
      if (!bImpacts.length || !forkD) return null;
      // If every impact delta is effectively zero, the branch line traces the exact
      // same y-values as the base series — don't render a redundant dashed overlay.
      if (bImpacts.every((imp) => Math.abs(Number(imp.delta_percent) || 0) < 0.01)) return null;
      const forkMs = forkD.getTime();
      // Anchor the branch path EXACTLY at the fork date with the parent line's interpolated
      // value at that timestamp, over all points, so it works wherever the fork falls (past
      // slice, future slice, or right on TODAY).
      let before = null,
        after = null;
      for (const p of allPoints) {
        const pms = new Date(p.date).getTime();
        if (pms <= forkMs) before = p;
        else {
          after = p;
          break;
        }
      }
      let forkValue;
      if (before && after) {
        const bMs = new Date(before.date).getTime();
        const aMs = new Date(after.date).getTime();
        forkValue =
          aMs > bMs
            ? before.value + ((forkMs - bMs) / (aMs - bMs)) * (after.value - before.value)
            : before.value;
      } else if (before) forkValue = before.value;
      else if (after) forkValue = after.value;
      else return null;
      const beyondFork = allPoints.filter((p) => new Date(p.date).getTime() > forkMs);
      const pts = [{ x: sx(forkD), y: clampY(forkValue) }];
      for (const d of beyondFork) {
        let delta = 0;
        for (const imp of bImpacts)
          if (new Date(imp.date) <= new Date(d.date)) delta = imp.delta_percent;
        const av = d.value * (1 + delta / 100);
        pts.push({ x: sx(d.date), y: clampY(av) });
      }
      if (pts.length < 2) return null;
      return {
        path: pts
          .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
          .join(" "),
        color: BRANCH_COLORS[bi % BRANCH_COLORS.length],
        branchId: br.runId,
      };
    })
    .filter(Boolean);
  // The extents feed the fade-out gradients and end at the resolution date (the last point).
  const allPts = [...past, ...future];
  const startX = allPts.length ? sx(allPts[0].date) : 0;
  const endX = allPts.length ? sx(allPts[allPts.length - 1].date) : 0;
  const futureEndX = future.length ? sx(future[future.length - 1].date) : endX;
  return {
    k,
    pastPath,
    futurePath,
    fill,
    confidenceFill,
    color: sColor,
    impactPath,
    branchPaths,
    yMin: mn,
    yMax: mx,
    data: s.data,
    startX,
    endX,
    futureEndX,
  };
}

/**
 * One axis of the chart: five ticks over the series' range, the value → y map, the slot color.
 *
 * @param {string} name  the series name
 * @param {object} s  the series entry (`yMin`, `yMax`, `unit`, `sourceUrl`)
 * @param {string} color  the slot color
 * @param {number} tsdH  the chart's height
 */
export function tsdAxisSlot(name, s, color, tsdH) {
  const mt = (s) =>
    Array.from({ length: 5 }, (_, i) => Math.round(s.yMin + ((s.yMax - s.yMin) * i) / 4));
  const yv = (s, v) => 4 + (tsdH - 8) * (1 - (v - s.yMin) / (s.yMax - s.yMin || 1));
  return {
    name,
    ticks: mt(s),
    yv: (v) => yv(s, v),
    color,
    unit: s.unit || "",
    sourceUrl: s.sourceUrl || null,
  };
}
